import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { avatarUrl } from "../lib/avatar";
import { API_BASE } from "../lib/api";
import Roadmap from "../components/Roadmap";
import LiveProofs from "../components/LiveProofs";
import StrategyCard from "../components/StrategyCard";
import { Edit, Download } from "lucide-react";

export default function Profile() {
  const { id } = useParams();
  const { user: me, updateUser } = useAuth();
  const [u, setU] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [editOpen, setEditOpen] = useState(false);

  const userId  = id === "me" || !id ? me?.id : id;
  const isOwner = me && me.id === userId;

  const load = useCallback(async () => {
    if (!userId) return;
    const [profileRes, milestonesRes, proofsRes, strategiesRes] = await Promise.all([
      supabase.from("profiles").select("*").eq("id", userId).single(),
      supabase.from("milestones").select("*").eq("user_id", userId).order("created_at", { ascending: true }),
      supabase.from("live_proofs").select("*").eq("user_id", userId).order("created_at", { ascending: false }),
      supabase.from("strategies")
        .select("*, author:profiles!user_id(id,name,avatar_url,participant_id), strategy_likes(user_id)")
        .eq("user_id", userId)
        .order("created_at", { ascending: false }),
    ]);
    const profile = profileRes.data;
    if (!profile) return;
    setU({
      ...profile,
      milestones:  milestonesRes.data  || [],
      live_proofs: proofsRes.data      || [],
    });
    setStrategies((strategiesRes.data || []).map((s) => ({
      ...s,
      like_count:  s.strategy_likes?.length || 0,
      liked_by_me: s.strategy_likes?.some((l) => l.user_id === me?.id) || false,
    })));
  }, [userId, me?.id]);

  useEffect(() => { load(); }, [load]);

  // Called by Roadmap/LiveProofs/EditModal with partial updates
  const onUserUpdate = async (updates) => {
    setU((prev) => ({ ...prev, ...updates }));
    if (isOwner) {
      // Sync scalar fields back to AuthContext (exclude array relations)
      const scalarUpdates = Object.fromEntries(
        Object.entries(updates).filter(([k]) => !["milestones","live_proofs"].includes(k))
      );
      if (Object.keys(scalarUpdates).length) await updateUser(scalarUpdates);
    }
  };

  if (!u) return <div className="p-10 font-hand text-3xl">loading profile…</div>;

  const pct = u.goal_amount > 0 ? Math.min(100, Math.round((u.current_amount / u.goal_amount) * 100)) : 0;
  const engScore = u.engagement_score || 0;
  const engTier =
    engScore >= 100 ? { label: "LEGEND", color: "#FF3333", bg: "#FF3333", text: "#fff" } :
    engScore >= 50  ? { label: "GRINDER", color: "#2B5CFF", bg: "#2B5CFF", text: "#fff" } :
    engScore >= 20  ? { label: "BUILDER", color: "#FFD600", bg: "#FFD600", text: "#121212" } :
                      { label: "ROOKIE", color: "#121212", bg: "#fff", text: "#121212" };

  const passParams = new URLSearchParams({
    name:             u.name || "Player",
    participant_id:   u.participant_id || "",
    goal_amount:      u.goal_amount || 0,
    strategy_summary: u.strategy_summary || "Building. Hustling. Shipping.",
    joined:           (u.created_at || "").slice(0, 10),
    avatar_url:       u.avatar_url || "",
  });
  const passUrl = `${API_BASE}/player-pass/${u.id}.pdf?${passParams}`;

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      <header className="brutal-card p-6 sm:p-8 relative">
        <div className="grid md:grid-cols-12 gap-6 items-center">
          <img src={avatarUrl(u.avatar_url, u.id)} alt={u.name} className="w-24 h-24 sm:w-32 sm:h-32 border-2 border-ink shadow-brutal-sm bg-cream blob" />
          <div className="md:col-span-7">
            <div className="font-mono text-xs uppercase tracking-widest text-ink/60">{u.participant_id}</div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl uppercase tracking-tight leading-none">{u.name}</h1>
            {u.location && <div className="font-hand text-2xl mt-1 text-ink/70">{u.location}</div>}
            {u.bio && <p className="mt-3 leading-relaxed">{u.bio}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              {(u.methods || []).map((m) => <span key={m} className="tag-pill">#{m}</span>)}
            </div>
          </div>
          <div className="md:col-span-3 flex flex-col gap-2">
            <a href={passUrl} target="_blank" rel="noopener noreferrer" className="brutal-btn brutal-btn-yellow" data-testid="download-pass-btn">
              <Download size={14} /> Download Player Pass
            </a>
            {isOwner && (
              <button onClick={() => setEditOpen(true)} className="brutal-btn" data-testid="edit-profile-btn">
                <Edit size={14} /> Edit profile
              </button>
            )}
          </div>
        </div>

        {/* ── Stats row ── */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {/* Target */}
          <div className="brutal-card-sm p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink/50 mb-1">Target</div>
            <div className="font-display font-extrabold text-3xl tracking-tight leading-none">
              ${Number(u.goal_amount || 0).toLocaleString()}
            </div>
          </div>

          {/* Earned */}
          <div className="brutal-card-sm p-4" style={{ background: "#FFD600" }}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink/60 mb-1">Earned</div>
            <div className="font-display font-extrabold text-3xl tracking-tight leading-none">
              ${Number(u.current_amount || 0).toLocaleString()}
            </div>
          </div>

          {/* Progress bar card */}
          <div className="brutal-card-sm p-4" style={{ background: "#2B5CFF", color: "#fff" }}>
            <div className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: "rgba(255,255,255,0.7)" }}>Progress</div>
            <div className="font-display font-extrabold text-3xl tracking-tight leading-none mb-3">{pct}%</div>
            {/* Bar track */}
            <div className="w-full h-3 relative overflow-hidden" style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)" }}>
              <div
                className="absolute left-0 top-0 h-full transition-all duration-700 ease-out"
                style={{ width: `${pct}%`, background: "#FFD600" }}
              />
            </div>
            <div className="font-mono text-[10px] mt-1" style={{ color: "rgba(255,255,255,0.6)" }}>
              ${Number(u.current_amount || 0).toLocaleString()} / ${Number(u.goal_amount || 0).toLocaleString()}
            </div>
          </div>

          {/* Engagement tier card */}
          <div
            className="brutal-card-sm p-4 relative overflow-hidden"
            style={{ background: engTier.bg, color: engTier.text, borderColor: "#121212" }}
          >
            <div className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: engTier.text, opacity: 0.65 }}>
              Engagement
            </div>
            <div className="font-display font-extrabold text-3xl tracking-tight leading-none mb-2">
              {engScore}
            </div>
            {/* Tier badge */}
            <div
              className="inline-block border-2 border-current px-2 py-0.5 font-mono font-bold text-[10px] uppercase tracking-widest"
              style={{ borderColor: engTier.text }}
            >
              {engTier.label}
            </div>
            {/* Decorative big number watermark */}
            <div
              className="absolute -right-3 -bottom-4 font-display font-extrabold text-7xl leading-none select-none pointer-events-none"
              style={{ opacity: 0.1, color: engTier.text }}
            >
              {engScore}
            </div>
          </div>
        </div>

        {u.strategy_summary && (
          <div className="mt-6 brutal-card-sm p-4 bg-cream">
            <div className="font-mono text-xs uppercase tracking-widest text-ink/60 mb-1">Strategy</div>
            <div className="font-hand text-2xl leading-snug">{u.strategy_summary}</div>
          </div>
        )}
      </header>

      <Roadmap    user={u} isOwner={isOwner} onUpdate={onUserUpdate} />
      <LiveProofs user={u} isOwner={isOwner} onUpdate={onUserUpdate} />

      <section>
        <h2 className="font-display font-extrabold text-3xl uppercase tracking-tight mb-4">
          Their <span className="highlight-marker">strategies</span>
        </h2>
        {strategies.length === 0 ? (
          <div className="brutal-card p-6 font-hand text-2xl">No strategies posted yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {strategies.map((s) => <StrategyCard key={s.id} s={s} compact />)}
          </div>
        )}
      </section>

      {editOpen && isOwner && <EditProfileModal user={u} onClose={() => setEditOpen(false)} onSave={onUserUpdate} />}
    </main>
  );
}


function EditProfileModal({ user, onClose, onSave }) {
  const [form, setForm] = useState({
    name:             user.name             || "",
    bio:              user.bio              || "",
    location:         user.location         || "",
    goal_amount:      user.goal_amount      || 0,
    strategy_summary: user.strategy_summary || "",
    methods:          (user.methods || []).join(", "),
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    const updates = {
      name:             form.name,
      bio:              form.bio,
      location:         form.location,
      goal_amount:      parseFloat(form.goal_amount || 0),
      strategy_summary: form.strategy_summary,
      methods:          form.methods.split(",").map((s) => s.trim()).filter(Boolean),
    };
    const { data, error } = await supabase.from("profiles").update(updates).eq("id", user.id).select().single();
    if (!error && data) onSave(data);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save}
        className="brutal-card p-6 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto"
        data-testid="edit-profile-form">
        <h3 className="font-display font-extrabold text-2xl uppercase tracking-tight">Edit profile</h3>
        <input className="brutal-input" placeholder="Name"     value={form.name}             onChange={set("name")} />
        <input className="brutal-input" placeholder="Location" value={form.location}          onChange={set("location")} />
        <textarea className="brutal-input min-h-[80px]" placeholder="Bio" value={form.bio}   onChange={set("bio")} />
        <input className="brutal-input" type="number" placeholder="Goal $" value={form.goal_amount} onChange={set("goal_amount")} />
        <textarea className="brutal-input min-h-[80px]" placeholder="Strategy summary" value={form.strategy_summary} onChange={set("strategy_summary")} />
        <input className="brutal-input" placeholder="Methods (comma separated)" value={form.methods} onChange={set("methods")} />
        <div className="flex gap-2">
          <button type="submit" className="brutal-btn brutal-btn-yellow flex-1" data-testid="save-profile-btn">Save</button>
          <button type="button" onClick={onClose} className="brutal-btn flex-1">Cancel</button>
        </div>
      </form>
    </div>
  );
}
